import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useParams, Link, useSearchParams } from 'react-router-dom';
import { Check, ChevronRight, ChevronLeft, Upload, Home, MapPin, DollarSign, Camera, X, Image as ImageIcon, Loader2, AlertCircle, Compass, Sparkles, Clock, ExternalLink, TrendingUp } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { CITIES, PROPERTY_TYPES, formatCOP } from '../mock';
import { PLANS } from '../mockPlans';
import { useToast } from '../hooks/use-toast';
import { api, imgUrl, friendlyError, parseValidationErrors, FIELD_LABELS } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import CityAutocomplete from '../components/CityAutocomplete';
import { FormError } from '../components/FormError';
import SEO from '../components/SEO';
import { track, EventTypes } from '../lib/analytics';

const STEPS = [
  { id: 1, label: 'Tipo', icon: Home },
  { id: 2, label: 'Ubicación', icon: MapPin },
  { id: 3, label: 'Detalles', icon: Camera },
  { id: 4, label: 'Fotos y video', icon: ImageIcon },
  { id: 5, label: 'Precio', icon: DollarSign }
];

// Cadena de upgrade para PUBLICACIÓN de propiedades (excluye Constructora,
// que es un plan basado en proyectos, no en publicaciones sueltas).
const PLAN_UPGRADE_CHAIN = ['gratis', 'starter', 'pro', 'inmobiliaria'];

export default function PublishProperty() {
  const navigate = useNavigate();
  const { toast } = useToast();
  // safeToast never throws — a broken toast provider must not crash the flow.
  const safeToast = (opts) => {
    try { toast(opts); } catch (e) { console.warn('[publish] toast failed:', e); }
  };
  const { user, refresh } = useAuth();
  const { id: editId } = useParams();
  const isEdit = Boolean(editId);
  const [searchParams] = useSearchParams();
  // Preselección de operación SOLO al crear un inmueble nuevo (nunca en edición).
  const opParam = searchParams.get('op');
  const initialOperation = (!isEdit && (opParam === 'venta' || opParam === 'arriendo')) ? opParam : 'venta';
  // Origen analítico: la publicación proviene de la herramienta de valoración.
  const fromValuation = searchParams.get('source') === 'valuation';
  const [step, setStep] = useState(1);
  const [done, setDone] = useState(false);
  const [publishedProp, setPublishedProp] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [error, setError] = useState('');
  // Upgrade "en caliente": al recibir 403 (límite de plan) mostramos un modal
  // que permite cambiar de plan gratis durante la Beta y seguir publicando.
  const [upgradeModal, setUpgradeModal] = useState({ open: false, currentName: '', nextId: null, nextName: '' });
  const [upgrading, setUpgrading] = useState(false);
  // Ref al publish más reciente, para reintentar tras el upgrade sin closure stale.
  const publishRef = useRef(null);
  const [fieldErrors, setFieldErrors] = useState({});  // { area: 'El área debe...', ... }
  const [loadingEdit, setLoadingEdit] = useState(isEdit);
  // Ref-based guard: bulletproof against React state-update delay on rapid double-clicks
  const submittingRef = useRef(false);
  // Refs to individual form fields so we can scrollIntoView on validation error
  const fieldRefs = useRef({});

  // Plan-aware photo cap. Admin & agents get the highest tier so they can
  // moderate properties on behalf of others. Free users get 5.
  const currentPlan = PLANS.find(p => p.id === (user?.plan_id || 'gratis')) || PLANS[0];
  const maxPhotos = user?.role === 'admin'
    ? 50
    : (typeof currentPlan.limits.photos === 'number' ? currentPlan.limits.photos : 50);

  const [data, setData] = useState({
    // operationMode: 'venta' | 'arriendo' | 'ambos'. `operation` es el campo
    // legacy primario (venta para 'ambos'). `price` es el precio de venta (o el
    // único), `rentPrice` el canon de arriendo cuando aplica.
    operationMode: initialOperation, operation: initialOperation, type: '', title: '',
    address: '', city: '', neighborhood: '', stratum: 4,
    beds: 2, baths: 2, parking: 1, area: 80, year: 2020,
    condition: '',
    description: '', features: [],
    ev_charger: 'unspecified',
    images: [],
    videos: [],
    price: '', rentPrice: '', adminFee: '',
    contactName: '', contactEmail: '', contactPhone: ''
  });

  // Lifted state from PricingHint so we can trigger the "above-range" modal from publish()
  const [pricingEstimate, setPricingEstimate] = useState(null); // {matched, price_min, price_max, ...}
  const [priceModal, setPriceModal] = useState({ open: false, overPct: 0, priceMax: 0 });
  const priceModalAcknowledgedRef = useRef(false); // avoid re-showing the modal after user confirmed

  // EDIT MODE — load existing property into state on mount
  useEffect(() => {
    if (!isEdit) return;
    let cancelled = false;
    (async () => {
      try {
        const p = await api.getProperty(editId);
        if (cancelled) return;

        // Ownership / admin guard. Public GET succeeds for anyone, so we
        // must enforce client-side that only the owner or an admin can edit.
        if (user && p.owner_id !== user.id && user.role !== 'admin') {
          toast({
            title: 'No tienes permiso para editar esta propiedad',
            description: 'Solo el propietario o un administrador pueden editarla.',
            variant: 'destructive',
          });
          navigate('/mi-cuenta');
          return;
        }

        const _ops = Array.isArray(p.operations) && p.operations.length ? p.operations : [p.operation || 'venta'];
        const _isDual = _ops.includes('venta') && _ops.includes('arriendo');
        const _mode = _isDual ? 'ambos' : (_ops[0] || 'venta');
        const _prices = p.prices || {};
        const _salePrice = _prices.venta ?? (p.operation === 'venta' ? p.price : (_mode === 'ambos' ? p.price : ''));
        const _rentPrice = _prices.arriendo ?? (p.operation === 'arriendo' ? p.price : '');
        setData({
          operationMode: _mode,
          operation: _isDual ? 'venta' : (p.operation || 'venta'),
          type: p.type || '',
          title: p.title || '',
          address: p.address || '',
          city: p.city || '',
          department: p.department || '',
          neighborhood: p.neighborhood || '',
          stratum: p.stratum || 4,
          beds: p.beds ?? 2,
          baths: p.baths ?? 2,
          parking: p.parking ?? 1,
          area: p.area ?? 80,
          year: p.year || 2020,
          condition: p.condition || '',
          description: p.description || '',
          features: p.features || [],
          ev_charger: p.ev_charger || 'unspecified',
          images: p.images || [],
          videos: p.videos || [],
          price: (_mode === 'arriendo' ? (p.price ?? _rentPrice) : _salePrice) ? String(_mode === 'arriendo' ? (p.price ?? _rentPrice) : _salePrice) : '',
          rentPrice: _rentPrice ? String(_rentPrice) : '',
          adminFee: p.admin_fee ? String(p.admin_fee) : '',
          contactName: '', contactEmail: '', contactPhone: '',
        });
      } catch (err) {
        toast({
          title: 'No pudimos cargar la propiedad',
          description: friendlyError(err, 'Verifica el enlace o vuelve a Mi cuenta.'),
          variant: 'destructive',
        });
        navigate('/mi-cuenta');
      } finally {
        if (!cancelled) setLoadingEdit(false);
      }
    })();
    return () => { cancelled = true; };
  }, [editId, isEdit, navigate, toast, user]);

  const setField = (k, v) => {
    setData(d => {
      const next = { ...d, [k]: v };
      // operationMode gobierna el campo legacy `operation` (primario).
      if (k === 'operationMode') {
        next.operation = v === 'arriendo' ? 'arriendo' : 'venta';
      }
      return next;
    });
    // Clear field-specific error as user types
    if (fieldErrors[k]) {
      setFieldErrors(fe => {
        const next = { ...fe };
        delete next[k];
        return next;
      });
    }
  };

  // Smoothly focus a field that has an error (mobile-friendly).
  const focusField = (fieldName) => {
    // Allow the step transition to settle before scrolling
    setTimeout(() => {
      const el = fieldRefs.current[fieldName];
      if (el && typeof el.scrollIntoView === 'function') {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        // Pull focus to the inner input for keyboard users
        const focusable = el.querySelector('input, textarea, select, button');
        if (focusable && typeof focusable.focus === 'function') {
          try { focusable.focus({ preventScroll: true }); } catch { /* ignore */ }
        }
      }
    }, 120);
  };

  // Client-side pre-flight validation. Returns {firstField, firstStep} on
  // first failure, or null if all good. Sets `fieldErrors` and the visible
  // banner `error` so the user gets immediate, specific feedback.
  const validateBeforeSubmit = () => {
    const errs = {};
    const required = [
      { key: 'type', check: !data.type, msg: 'Selecciona el tipo de inmueble.' },
      { key: 'title', check: !data.title || data.title.trim().length < 5, msg: 'El título debe tener al menos 5 caracteres.' },
      { key: 'address', check: !data.address || data.address.trim().length < 3, msg: 'Ingresa una dirección válida.' },
      { key: 'city', check: !data.city, msg: 'Selecciona la ciudad o municipio.' },
      { key: 'description', check: data.description && data.description.trim().length > 0 && data.description.trim().length < 10, msg: 'La descripción debe tener al menos 10 caracteres (o déjala vacía y la generamos automáticamente).' },
      { key: 'area', check: !data.area || +data.area < 10 || +data.area > 5000, msg: 'El área debe estar entre 10 y 5.000 m².' },
      { key: 'condition', check: !data.condition, msg: 'Selecciona el estado del inmueble.' },
      { key: 'year', check: !data.year || +data.year < 1900 || +data.year > 2100, msg: 'Ingresa un año de construcción válido.' },
      { key: 'beds', check: +data.beds < 0 || +data.beds > 20, msg: 'Las habitaciones deben ser entre 0 y 20.' },
      { key: 'baths', check: +data.baths < 0 || +data.baths > 20, msg: 'Los baños deben ser entre 0 y 20.' },
      { key: 'parking', check: +data.parking < 0 || +data.parking > 20, msg: 'Los parqueaderos deben ser entre 0 y 20.' },
      { key: 'price', check: !data.price || +data.price <= 0, msg: data.operationMode === 'ambos' ? 'Ingresa el precio de venta.' : 'El precio debe ser mayor a 0.' },
      { key: 'rentPrice', check: data.operationMode === 'ambos' && (!data.rentPrice || +data.rentPrice <= 0), msg: 'Ingresa el precio de arriendo mensual.' },
      { key: 'images', check: !data.images || data.images.length === 0, msg: 'Sube al menos 1 foto.' },
    ];
    for (const r of required) {
      if (r.check) errs[r.key] = r.msg;
    }
    if (Object.keys(errs).length === 0) return null;
    setFieldErrors(errs);
    const keys = Object.keys(errs);
    const firstKey = keys[0];
    const firstStep = FIELD_LABELS[firstKey]?.step || 1;
    const banner = keys.length === 1
      ? errs[firstKey]
      : `Revisa los siguientes campos:\n• ${keys.map((k) => errs[k]).join('\n• ')}`;
    setError(banner);
    setStep(firstStep);
    focusField(firstKey);
    return { firstField: firstKey, firstStep };
  };

  const publish = async () => {
    // Hard guard against double-submit (covers React state-update delay and Safari double-tap)
    if (submittingRef.current) return;

    setError('');
    setFieldErrors({});
    if (uploading) { setError('Espera a que terminen de subir las fotos.'); return; }

    // Client-side validation first — catches the most common failures (area<10,
    // description<10, missing fields) before we even hit the network.
    const preflight = validateBeforeSubmit();
    if (preflight) return;

    // Friendly price-calibration modal: if the user's price is meaningfully
    // above the suggested range for the sector, ask before submitting.
    // Skip if already acknowledged in this session (avoids nagging on retry).
    const priceNum = +data.price || 0;
    const pmax = pricingEstimate?.matched ? +pricingEstimate.price_max || 0 : 0;
    if (
      !priceModalAcknowledgedRef.current &&
      pmax > 0 && priceNum > pmax * 1.10
    ) {
      const overPct = Math.round(((priceNum - pmax) / pmax) * 100);
      setPriceModal({ open: true, overPct, priceMax: pmax });
      return; // wait for user's choice in the modal
    }

    // Sanitize images: drop any null/undefined/non-string entries that may have
    // sneaked in from a failed upload response. Prevents backend 422 + weird JS
    // crashes on <img src={imgUrl(null)}> combinations.
    const cleanImages = (data.images || []).filter(x => typeof x === 'string' && x.length > 0);
    if (cleanImages.length !== (data.images || []).length) {
      setData(d => ({ ...d, images: cleanImages }));
    }

    // Enforce plan photo cap client-side too (defensive — backend also enforces)
    if (cleanImages.length > maxPhotos) {
      const msg = `Tu plan (${currentPlan.name}) permite máximo ${maxPhotos} fotos. Tienes ${cleanImages.length}. Elimina ${cleanImages.length - maxPhotos} y vuelve a publicar.`;
      setError(msg);
      safeToast({ title: 'Demasiadas fotos', description: msg, variant: 'destructive' });
      return;
    }

    submittingRef.current = true;
    setPublishing(true);
    try {
      const _mode = data.operationMode;
      const _operations = _mode === 'ambos' ? ['venta', 'arriendo'] : [data.operation];
      const _prices = _mode === 'ambos'
        ? { venta: +data.price, arriendo: +data.rentPrice }
        : { [data.operation]: +data.price };
      const payload = {
        operation: data.operation,
        operations: _operations,
        prices: _prices,
        ev_charger: data.ev_charger || 'unspecified',
        type: data.type,
        title: data.title.trim(),
        description: (data.description || `${data.type} en ${data.city}`).trim(),
        address: data.address.trim(),
        city: data.city,
        department: data.department || undefined,
        neighborhood: data.neighborhood?.trim() || undefined,
        price: +data.price,
        admin_fee: +data.adminFee || 0,
        beds: +data.beds, baths: +data.baths, parking: +data.parking,
        area: +data.area, stratum: +data.stratum, year: +data.year,
        condition: data.condition || null,
        features: data.features,
        images: cleanImages,
        videos: data.videos,
      };

      if (isEdit) {
        await api.updateProperty(editId, payload);
        safeToast({
          title: 'Cambios guardados',
          description: 'Tu propiedad fue actualizada correctamente.',
        });
        navigate(`/propiedad/${editId}`);
        return;
      }

      const created = await api.createProperty(payload);
      safeToast({
        title: created?.status === 'pendiente'
          ? 'Publicación recibida'
          : '¡Propiedad publicada!',
        description: created?.status === 'pendiente'
          ? 'Tu publicación está en revisión. Te avisaremos en menos de 24 horas.'
          : 'Tu propiedad ya está visible en habita.co',
      });
      setPublishedProp(created);
      setDone(true);
      // Embudo de valoración: publicación creada desde ValoraGratis.
      if (fromValuation && created?.id) {
        try {
          track(EventTypes.VALUATION_PUBLISH_CREATED, {
            property_id: created.id, operation: data.operation,
          });
        } catch { /* analytics no debe romper el flujo */ }
      }
    } catch (err) {
      // Full-fidelity log so devs can diagnose from browser console + PostHog.
      console.error('[publish] createProperty failed:', {
        status: err?.status,
        message: err?.message,
        path: err?.path,
        detail: err?.data?.detail,
      });
      // ── Upgrade EN CALIENTE: límite de plan alcanzado (HTTP 403) ──
      // En vez de un toast genérico, ofrecemos cambiar de plan gratis durante
      // la Beta y seguir publicando sin perder los datos ya ingresados.
      if (err?.status === 403 && !isEdit) {
        const idx = PLAN_UPGRADE_CHAIN.indexOf(currentPlan.id);
        const nextId = idx >= 0 ? PLAN_UPGRADE_CHAIN[idx + 1] : null;
        const nextPlan = nextId ? PLANS.find(p => p.id === nextId) : null;
        if (nextPlan) {
          setUpgradeModal({ open: true, currentName: currentPlan.name, nextId: nextPlan.id, nextName: nextPlan.name });
          return; // no mostramos el toast genérico
        }
      }
      try {
        // Try to extract field-level Pydantic errors (translated to Spanish)
        const fieldErrs = parseValidationErrors(err);
        if (fieldErrs && fieldErrs.length) {
          const byField = {};
          for (const e of fieldErrs) byField[e.field] = e.message;
          setFieldErrors(byField);
          // Jump to the step of the FIRST error and scroll into view
          const first = fieldErrs[0];
          if (first.step) setStep(first.step);
          focusField(first.field);
          // Show all errors in the banner (one per line)
          const banner = fieldErrs.length === 1
            ? fieldErrs[0].message
            : `Revisa los siguientes campos:\n• ${fieldErrs.map((e) => e.message).join('\n• ')}`;
          setError(banner);
          safeToast({
            title: 'Revisa los datos del formulario',
            description: fieldErrs[0].message,
            variant: 'destructive',
          });
        } else {
          const friendly = friendlyError(err, isEdit ? 'No pudimos guardar los cambios. Intenta nuevamente.' : 'No pudimos completar la publicación. Intenta nuevamente.');
          setError(friendly);
          safeToast({ title: isEdit ? 'No pudimos guardar' : 'No pudimos publicar', description: friendly, variant: 'destructive' });
        }
      } catch (handlerErr) {
        // If the error handler itself throws, DO NOT let it reach the
        // ErrorBoundary — surface something sensible.
        console.error('[publish] error-handler crashed:', handlerErr);
        setError('Error al guardar la propiedad. Intenta nuevamente.');
      }
    } finally {
      submittingRef.current = false;
      setPublishing(false);
    }
  };

  // Mantener publishRef apuntando al publish más reciente (evita closure stale
  // al reintentar tras el upgrade, cuando ya cambió user.plan_id).
  publishRef.current = publish;

  // Upgrade en caliente: suscribe (gratis en Beta) → refresca usuario → cierra
  // el modal → reintenta la publicación con los MISMOS datos, sin recargar.
  const handleUpgrade = async () => {
    if (upgrading || !upgradeModal.nextId) return;
    setUpgrading(true);
    try {
      await api.subscribe({ plan_id: upgradeModal.nextId, billing: 'mes' });
      await refresh();
      safeToast({
        title: '¡Plan actualizado!',
        description: `Ahora tienes el plan ${upgradeModal.nextName} (gratis durante la Beta). Continuamos con tu publicación.`,
      });
      setUpgradeModal({ open: false, currentName: '', nextId: null, nextName: '' });
      // Reintento con el publish fresco tras el re-render por el cambio de plan.
      setTimeout(() => { publishRef.current && publishRef.current(); }, 150);
    } catch (e) {
      safeToast({ title: 'No pudimos cambiar el plan', description: friendlyError(e), variant: 'destructive' });
    } finally {
      setUpgrading(false);
    }
  };

  const toggleFeature = (f) => {
    setData(d => ({ ...d, features: d.features.includes(f) ? d.features.filter(x => x !== f) : [...d.features, f] }));
  };

  const FEATURES_LIST = ['Piscina', 'Gimnasio', 'Salón social', 'Portería 24/7', 'Aire acondicionado', 'Balcón', 'Ascensor', 'Jardín', 'BBQ', 'Chimenea', 'Estudio', 'Cuarto de servicio', 'Amoblado', 'Vista al mar', 'Internet incluido', 'Gas natural'];

  const _validateStep = (s) => {
    const rules = [];
    if (s === 1) {
      rules.push({ key: 'type', check: !data.type, msg: 'Selecciona el tipo de propiedad.' });
    } else if (s === 2) {
      rules.push({ key: 'title', check: !data.title || data.title.trim().length < 5, msg: 'El título debe tener al menos 5 caracteres.' });
      rules.push({ key: 'address', check: !data.address || data.address.trim().length < 4, msg: 'Ingresa una dirección.' });
      rules.push({ key: 'city', check: !data.city, msg: 'Selecciona la ciudad.' });
    } else if (s === 3) {
      rules.push({ key: 'area', check: !data.area || +data.area < 10 || +data.area > 5000, msg: 'El área debe estar entre 10 y 5.000 m².' });
      rules.push({ key: 'condition', check: !data.condition, msg: 'Selecciona el estado del inmueble.' });
      rules.push({ key: 'year', check: !data.year || +data.year < 1900 || +data.year > 2100, msg: 'Ingresa un año de construcción válido.' });
    }
    const failed = rules.find(r => r.check);
    if (failed) {
      setFieldErrors(prev => ({ ...prev, [failed.key]: failed.msg }));
      const el = fieldRefs.current[failed.key];
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return false;
    }
    return true;
  };

  const next = () => {
    if (!_validateStep(step)) return;
    setStep(s => Math.min(5, s + 1));
  };
  const prev = () => setStep(s => Math.max(1, s - 1));

  // Per-file upload progress: [{name, status: 'queued|uploading|done|error', error?}]
  const [uploadProgress, setUploadProgress] = useState([]);

  const handleImages = async (e) => {
    const files = Array.from(e.target.files || []);
    // Reset the input so the user can retry with the same files if needed.
    e.target.value = '';
    if (!files.length) return;

    // Plan-aware pre-check: block BEFORE uploading anything so the user gets
    // an immediate, specific message rather than a generic backend 400 later.
    const currentCount = (data.images || []).length;
    const remaining = Math.max(0, maxPhotos - currentCount);
    if (remaining === 0) {
      const msg = `Ya alcanzaste el máximo de ${maxPhotos} fotos para tu plan (${currentPlan.name}). Elimina alguna o mejora tu plan para subir más.`;
      setError(msg);
      toast({ title: 'Límite de fotos alcanzado', description: msg, variant: 'destructive' });
      return;
    }
    let toUpload = files;
    if (files.length > remaining) {
      const msg = `Solo puedes subir ${remaining} foto${remaining === 1 ? '' : 's'} más (máximo ${maxPhotos} para tu plan). Se subirán las primeras ${remaining} que elegiste.`;
      setError(msg);
      toast({ title: 'Superaste el límite de fotos', description: msg, variant: 'destructive' });
      toUpload = files.slice(0, remaining);
    } else {
      setError('');
    }

    setUploading(true);

    const initial = toUpload.map((f, i) => ({
      idx: i,
      name: f.name,
      size: f.size,
      status: 'queued',
    }));
    setUploadProgress(initial);

    /** Uploads one file with up to 2 retries for transient errors. */
    const uploadOne = async (file, idx) => {
      setUploadProgress(p => p.map(x => x.idx === idx ? { ...x, status: 'uploading' } : x));
      let lastErr = null;
      for (let attempt = 1; attempt <= 3; attempt++) {
        try {
          const r = await api.uploadImage(file);
          if (!r?.url) throw new Error('El servidor no devolvió una URL válida para la imagen.');
          setUploadProgress(p => p.map(x => x.idx === idx ? { ...x, status: 'done', url: r.url } : x));
          return r.url;
        } catch (err) {
          lastErr = err;
          // Don't retry on user errors (400, 413, 415)
          if (err?.status && err.status >= 400 && err.status < 500 && err.status !== 0 && err.status !== 408 && err.status !== 429) {
            break;
          }
          // Exponential backoff: 800ms, 1600ms
          if (attempt < 3) await new Promise(r => setTimeout(r, 800 * attempt));
        }
      }
      const msg = friendlyError(lastErr, 'No pudimos procesar esta imagen. Intenta con otra o revisa tu conexión.');
      setUploadProgress(p => p.map(x => x.idx === idx ? { ...x, status: 'error', error: msg } : x));
      throw lastErr;
    };

    // Limited concurrency to avoid overwhelming the server (mobile uplinks die fast).
    const CONCURRENCY = 3;
    const uploaded = [];
    const failed = [];
    const queue = toUpload.map((f, i) => ({ f, i }));
    const workers = Array.from({ length: Math.min(CONCURRENCY, queue.length) }, async () => {
      while (queue.length) {
        const nextItem = queue.shift();
        if (!nextItem) break;
        try {
          const url = await uploadOne(nextItem.f, nextItem.i);
          uploaded.push(url);
        } catch (err) {
          // Log per-file failure so it appears in browser console + PostHog
          console.warn('[publish] image upload failed:', nextItem.f?.name, err?.message || err);
          failed.push(nextItem.f.name);
        }
      }
    });
    await Promise.all(workers);

    if (uploaded.length > 0) {
      setData(d => ({ ...d, images: [...(d.images || []), ...uploaded] }));
    }
    if (failed.length > 0) {
      setError(
        uploaded.length === 0
          ? `No pudimos subir ninguna imagen. Revisa tu conexión o intenta con archivos más pequeños (máx. 25 MB, JPG/PNG/WEBP/HEIC).`
          : `Se subieron ${uploaded.length} de ${toUpload.length} fotos. ${failed.length} falla${failed.length > 1 ? 'ron' : ''} — puedes reintentarlo con esas.`,
      );
    }
    setUploading(false);
    // Auto-clear progress after 3s if all succeeded
    if (failed.length === 0) {
      setTimeout(() => setUploadProgress([]), 3000);
    }
  };

  if (done) {
    const isActive = publishedProp?.status === 'activa';
    const propId = publishedProp?.id;
    const cover = publishedProp?.images?.[0];
    const searchUrl = `/buscar?op=${encodeURIComponent(publishedProp?.operation || data.operation)}&city=${encodeURIComponent(publishedProp?.city || data.city)}&type=${encodeURIComponent(publishedProp?.type || data.type)}`;

    return (
      <div className="min-h-screen bg-habita-cream">
        <Header />
        <div className="max-w-3xl mx-auto px-4 py-14">
          {/* Hero de confirmación */}
          <div className="text-center">
            <div className={`w-20 h-20 rounded-full mx-auto flex items-center justify-center ${isActive ? 'bg-habita-mint' : 'bg-amber-100'}`} data-testid="publish-success-hero">
              {isActive ? <Check className="w-10 h-10 text-habita" /> : <Clock className="w-9 h-9 text-amber-600" />}
            </div>
            <h1 className="font-serif text-4xl lg:text-5xl font-bold text-habita-dark mt-6" data-testid="publish-success-title">
              {isActive ? '¡Tu propiedad ya está publicada!' : 'Tu publicación quedó en revisión'}
            </h1>
            <p className="text-gray-600 mt-3 max-w-xl mx-auto">
              {isActive
                ? 'Ya es visible en habita.co y el Habita Advisor la considera para las recomendaciones que hace a los usuarios que buscan lo que ofreces.'
                : 'Nuestro equipo la revisará en menos de 24 horas. Te avisaremos por email en cuanto esté activa — y en ese momento el Advisor la incluirá automáticamente en sus recomendaciones.'}
            </p>
          </div>

          {/* Ficha resumida de la propiedad publicada */}
          {publishedProp && (
            <div className="mt-10 bg-white rounded-3xl border border-gray-100 shadow-sm overflow-hidden" data-testid="publish-success-card">
              <div className="grid sm:grid-cols-[220px_1fr]">
                <div className="aspect-[4/3] sm:aspect-auto bg-gray-100">
                  {cover
                    ? <img src={imgUrl(cover)} alt="" className="w-full h-full object-cover" />
                    : <div className="w-full h-full grid place-items-center text-gray-300"><ImageIcon className="w-10 h-10" /></div>}
                </div>
                <div className="p-5">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest px-2 py-1 rounded ${isActive ? 'bg-habita-mint text-habita' : 'bg-amber-100 text-amber-700'}`} data-testid="publish-success-status">
                      {isActive ? 'Activa' : 'Pendiente de revisión'}
                    </span>
                    <span className="text-[10px] uppercase tracking-widest text-gray-500 font-semibold">
                      {publishedProp.operation === 'arriendo' ? 'Arriendo' : 'Venta'}
                    </span>
                  </div>
                  <h2 className="font-serif text-xl font-bold text-habita-dark mt-2 line-clamp-2">{publishedProp.title}</h2>
                  <p className="text-xs text-gray-500 mt-0.5">{publishedProp.city}{publishedProp.neighborhood ? ` · ${publishedProp.neighborhood}` : ''}</p>
                  <p className="font-serif text-2xl font-bold text-habita mt-3">
                    {formatCOP(publishedProp.price)}{publishedProp.operation === 'arriendo' && <span className="text-xs font-normal text-gray-500"> /mes</span>}
                  </p>

                  {/* Advisor consistency signal */}
                  <div className={`mt-4 rounded-xl p-3 border ${isActive ? 'bg-habita-mint/50 border-habita/20' : 'bg-amber-50 border-amber-200'}`} data-testid="publish-success-advisor-note">
                    <div className="flex items-start gap-2">
                      <Compass className={`w-4 h-4 mt-0.5 shrink-0 ${isActive ? 'text-habita' : 'text-amber-600'}`} />
                      <p className="text-xs text-habita-dark leading-relaxed">
                        {isActive ? (
                          <>
                            <span className="font-semibold">El Advisor ya la conoce.</span> A cada usuario cuya búsqueda coincida con tu propiedad, se la mostraremos con una razón clara y personalizada.
                          </>
                        ) : (
                          <>
                            <span className="font-semibold">Aún no aparece en el Advisor.</span> En cuanto se apruebe, entrará al pipeline de recomendaciones automáticamente — sin que tengas que hacer nada más.
                          </>
                        )}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* CTAs */}
          <div className="mt-8 grid sm:grid-cols-2 gap-3">
            {propId && isActive && (
              <Button
                onClick={() => navigate(`/propiedad/${propId}`)}
                className="btn-habita rounded-full h-12"
                data-testid="publish-success-view-property"
              >
                Ver mi publicación <ExternalLink className="w-4 h-4 ml-1.5" />
              </Button>
            )}
            <Button
              onClick={() => navigate('/mi-cuenta?tab=properties')}
              variant="outline"
              className="rounded-full h-12"
              data-testid="publish-success-my-properties"
            >
              Ir a mis publicaciones
            </Button>
            {isActive && (
              <Button
                onClick={() => navigate(searchUrl)}
                variant="outline"
                className="rounded-full h-12"
                data-testid="publish-success-view-in-search"
              >
                <Sparkles className="w-4 h-4 mr-1.5" /> Ver cómo aparece en el Advisor
              </Button>
            )}
            <Button
              onClick={() => { setDone(false); setPublishedProp(null); setStep(1); setData({
                operationMode: 'venta', operation: 'venta', type: '', title: '',
                address: '', city: '', neighborhood: '', stratum: 4,
                beds: 2, baths: 2, parking: 1, area: 80, year: 2020,
                description: '', features: [], ev_charger: 'unspecified',
                images: [], videos: [],
                price: '', rentPrice: '', adminFee: '',
                contactName: '', contactEmail: '', contactPhone: ''
              }); }}
              variant="outline"
              className="rounded-full h-12"
              data-testid="publish-success-publish-another"
            >
              Publicar otra propiedad
            </Button>
          </div>

          <p className="text-center text-xs text-gray-500 mt-6">
            ¿Necesitas cambiar algo? Puedes editar la publicación cuando quieras desde <Link to="/mi-cuenta?tab=properties" className="text-habita font-semibold hover:underline">Mis publicaciones</Link>.
          </p>
        </div>
        <Footer />
      </div>
    );
  }

  if (loadingEdit) {
    return (
      <div className="min-h-screen bg-habita-cream">
        <Header />
        <div className="max-w-4xl mx-auto px-4 lg:px-8 py-20 text-center">
          <Loader2 className="w-10 h-10 text-habita animate-spin mx-auto" />
          <p className="text-gray-600 mt-4">Cargando tu propiedad...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-habita-cream" translate="no">
      <SEO
        title={isEdit ? 'Editar publicación' : 'Publicar propiedad gratis'}
        description="Publica gratis tu apartamento, casa o local en habita.co. Llega a más de 2 millones de compradores e inquilinos en Colombia con recomendaciones del Habita Advisor."
      />
      <Header />
      <div className="max-w-4xl mx-auto px-4 lg:px-8 py-10">
        <div className="text-center mb-10">
          <span className="text-xs uppercase tracking-widest text-amber-600 font-semibold">
            {isEdit ? 'Editar publicación' : 'Publicar gratis'}
          </span>
          <h1 className="font-serif text-4xl lg:text-5xl font-bold text-habita-dark mt-2">
            {isEdit ? 'Edita tu propiedad' : 'Publica tu propiedad'}
          </h1>
          <p className="text-gray-600 mt-3">
            {isEdit
              ? 'Actualiza fotos, precio, descripción o detalles cuando quieras.'
              : 'Llega a más de 2 millones de potenciales compradores e inquilinos.'}
          </p>
        </div>

        {/* Stepper */}
        <div className="flex items-center justify-between mb-10">
          {STEPS.map((s, i) => {
            const active = step === s.id;
            const passed = step > s.id;
            return (
              <React.Fragment key={s.id}>
                <div className="flex flex-col items-center">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors ${active ? 'bg-habita text-white' : passed ? 'bg-habita-mint text-habita' : 'bg-white border border-gray-200 text-gray-400'}`}>
                    {passed ? <Check className="w-5 h-5" /> : <s.icon className="w-5 h-5" />}
                  </div>
                  <span className={`text-xs mt-2 font-medium ${active ? 'text-habita' : 'text-gray-500'}`}>{s.label}</span>
                </div>
                {i < STEPS.length - 1 && <div className={`flex-1 h-0.5 mx-2 ${passed ? 'bg-habita' : 'bg-gray-200'}`} />}
              </React.Fragment>
            );
          })}
        </div>

        <div className="bg-white rounded-3xl shadow-sm border border-gray-100 p-6 lg:p-10">
          {/* STEP 1 */}
          {step === 1 && (
            <div>
              <h2 className="font-serif text-2xl font-bold text-habita-dark">¿Qué quieres hacer con este inmueble?</h2>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-5">
                {[
                  { v: 'venta', l: 'Vender', d: 'Vende tu propiedad' },
                  { v: 'arriendo', l: 'Arrendar', d: 'Encuentra inquilinos' },
                  { v: 'ambos', l: 'Vender y arrendar', d: 'Un mismo inmueble, ambas modalidades' },
                ].map(o => (
                  <button
                    key={o.v}
                    type="button"
                    data-testid={`publish-mode-${o.v}`}
                    onClick={() => setField('operationMode', o.v)}
                    className={`p-5 rounded-2xl border-2 text-left transition-all ${data.operationMode === o.v ? 'border-habita bg-habita-mint' : 'border-gray-200 hover:border-gray-300'}`}
                  >
                    <p className="font-semibold text-lg text-habita-dark">{o.l}</p>
                    <p className="text-xs text-gray-600 mt-1">{o.d}</p>
                  </button>
                ))}
              </div>
              <h3 className="font-semibold mt-8 mb-4 text-habita-dark">Tipo de inmueble</h3>
              <div ref={(el) => { if (el) fieldRefs.current.type = el; }} className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {PROPERTY_TYPES.map(t => (
                  <button key={t.value} onClick={() => setField('type', t.value)} data-testid={`publish-type-${t.value}`} className={`p-3 rounded-xl border-2 text-sm font-medium transition-all ${data.type === t.value ? 'border-habita bg-habita-mint text-habita' : (fieldErrors.type ? 'border-rose-300 hover:border-rose-400 text-gray-700' : 'border-gray-200 hover:border-gray-300 text-gray-700')}`}>
                    {t.label}
                  </button>
                ))}
              </div>
              {fieldErrors.type && <FormError msg={fieldErrors.type} testid="field-error-type" />}
            </div>
          )}

          {/* STEP 2 */}
          {step === 2 && (
            <div className="space-y-5">
              <h2 className="font-serif text-2xl font-bold text-habita-dark">¿Dónde queda?</h2>
              <div ref={(el) => { if (el) fieldRefs.current.title = el; }} data-testid="field-title-wrap">
                <Label className="text-xs uppercase tracking-widest text-gray-500">Título de la publicación</Label>
                <Input
                  value={data.title}
                  onChange={e => setField('title', e.target.value)}
                  placeholder="Ej: Apartamento moderno con vista panorámica"
                  className={`mt-1.5 h-12 ${fieldErrors.title ? 'border-rose-500 focus-visible:ring-rose-400' : ''}`}
                  data-testid="publish-field-title"
                  aria-invalid={!!fieldErrors.title}
                />
                {fieldErrors.title && <FormError msg={fieldErrors.title} testid="field-error-title" />}
              </div>
              <div ref={(el) => { if (el) fieldRefs.current.address = el; }}>
                <Label className="text-xs uppercase tracking-widest text-gray-500">Dirección</Label>
                <Input
                  value={data.address}
                  onChange={e => setField('address', e.target.value)}
                  placeholder="Carrera 43A #7-30"
                  className={`mt-1.5 h-12 ${fieldErrors.address ? 'border-rose-500 focus-visible:ring-rose-400' : ''}`}
                  data-testid="publish-field-address"
                  aria-invalid={!!fieldErrors.address}
                />
                {fieldErrors.address && <FormError msg={fieldErrors.address} testid="field-error-address" />}
              </div>
              <div className="grid md:grid-cols-3 gap-4">
                <div ref={(el) => { if (el) fieldRefs.current.city = el; }}>
                  <Label className="text-xs uppercase tracking-widest text-gray-500">Ciudad o municipio</Label>
                  <CityAutocomplete
                    value={data.city}
                    onChange={(name) => setField('city', name)}
                    onSelect={(m) => { setField('city', m.name); setField('department', m.dept); }}
                    className={`mt-1.5 ${fieldErrors.city ? 'ring-2 ring-rose-400 rounded-md' : ''}`}
                    testId="publish-city"
                  />
                  {data.department && <p className="text-[11px] text-gray-500 mt-1">{data.department}</p>}
                  {fieldErrors.city && <FormError msg={fieldErrors.city} testid="field-error-city" />}
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-gray-500">Barrio</Label>
                  <Input value={data.neighborhood} onChange={e => setField('neighborhood', e.target.value)} placeholder="El Poblado" className="mt-1.5 h-12" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-gray-500">Estrato</Label>
                  <select value={data.stratum} onChange={e => setField('stratum', +e.target.value)} className="mt-1.5 w-full h-12 rounded-md border border-input bg-transparent px-3 text-sm outline-none focus:ring-2 focus:ring-habita">
                    {[1, 2, 3, 4, 5, 6].map(n => <option key={n} value={n}>Estrato {n}</option>)}
                  </select>
                </div>
              </div>
            </div>
          )}

          {/* STEP 3 */}
          {step === 3 && (
            <div className="space-y-5">
              <h2 className="font-serif text-2xl font-bold text-habita-dark">Detalles del inmueble</h2>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                {[
                  ['beds', 'Habitaciones'], ['baths', 'Baños'], ['parking', 'Parqueaderos'], ['area', 'Área m²'], ['year', 'Año construc.']
                ].map(([k, label]) => (
                  <div key={k} ref={(el) => { if (el) fieldRefs.current[k] = el; }}>
                    <Label className="text-xs uppercase tracking-widest text-gray-500">{label}</Label>
                    <Input
                      type="number"
                      value={data[k]}
                      onChange={e => setField(k, e.target.value === '' ? '' : +e.target.value)}
                      className={`mt-1.5 h-12 ${fieldErrors[k] ? 'border-rose-500 focus-visible:ring-rose-400' : ''}`}
                      data-testid={`publish-field-${k}`}
                      aria-invalid={!!fieldErrors[k]}
                      inputMode="numeric"
                    />
                    {fieldErrors[k] && <FormError msg={fieldErrors[k]} testid={`field-error-${k}`} />}
                  </div>
                ))}
              </div>

              {/* Estado del inmueble — habilita el motor de valoración */}
              <div ref={(el) => { if (el) fieldRefs.current.condition = el; }}>
                <Label className="text-xs uppercase tracking-widest text-gray-500">Estado del inmueble *</Label>
                <select
                  value={data.condition}
                  onChange={e => setField('condition', e.target.value)}
                  className={`mt-1.5 h-12 w-full rounded-md border bg-white px-3 text-sm outline-none focus:ring-2 ${fieldErrors.condition ? 'border-rose-500 focus:ring-rose-400' : 'border-input focus:ring-habita'}`}
                  data-testid="publish-field-condition"
                  aria-invalid={!!fieldErrors.condition}
                >
                  <option value="">— Selecciona una opción —</option>
                  <option value="remodelado">Remodelado</option>
                  <option value="bueno">Bueno</option>
                  <option value="original">Original</option>
                  <option value="para_remodelar">Para remodelar</option>
                </select>
                {fieldErrors.condition && <FormError msg={fieldErrors.condition} testid="field-error-condition" />}
                <p className="text-[11px] text-gray-500 mt-1">
                  Nos ayuda a calibrar el precio sugerido de tu sector.
                </p>
              </div>

              <div ref={(el) => { if (el) fieldRefs.current.description = el; }}>
                <Label className="text-xs uppercase tracking-widest text-gray-500">Descripción</Label>
                <textarea
                  value={data.description}
                  onChange={e => setField('description', e.target.value)}
                  rows={5}
                  className={`mt-1.5 w-full rounded-md border bg-transparent px-3 py-2 text-sm outline-none focus:ring-2 ${fieldErrors.description ? 'border-rose-500 focus:ring-rose-400' : 'border-input focus:ring-habita'}`}
                  placeholder="Describe lo más atractivo de tu propiedad (mínimo 10 caracteres)..."
                  data-testid="publish-field-description"
                  aria-invalid={!!fieldErrors.description}
                />
                {fieldErrors.description
                  ? <FormError msg={fieldErrors.description} testid="field-error-description" />
                  : <p className="text-[11px] text-gray-500 mt-1">Opcional · Si la dejas vacía, generaremos una automáticamente.</p>}
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-gray-500">Características</Label>
                <p className="text-[11px] text-gray-500 mt-1">
                  <Sparkles className="w-3 h-3 inline text-habita mr-0.5" />
                  El Advisor prioriza propiedades con <span className="font-semibold text-habita-dark">Ascensor</span>, <span className="font-semibold text-habita-dark">Portería 24/7</span> y <span className="font-semibold text-habita-dark">Balcón</span> cuando los usuarios las marcan como indispensables.
                </p>
                <div className="flex flex-wrap gap-2 mt-2">
                  {FEATURES_LIST.map(f => (
                    <button key={f} onClick={() => toggleFeature(f)} type="button" className={`px-4 py-2 rounded-full text-sm border transition-colors ${data.features.includes(f) ? 'bg-habita text-white border-habita' : 'border-gray-200 hover:border-habita'}`}>
                      {f}
                    </button>
                  ))}
                </div>
              </div>

              {/* Cargador para vehículo eléctrico — campo estructurado (NO amenity) */}
              <div>
                <Label className="text-xs uppercase tracking-widest text-gray-500">¿Cuenta con cargador para vehículo eléctrico?</Label>
                <select
                  value={data.ev_charger}
                  onChange={e => setField('ev_charger', e.target.value)}
                  className="mt-1.5 h-12 w-full rounded-md border border-input bg-white px-3 text-sm outline-none focus:ring-2 focus:ring-habita"
                  data-testid="publish-field-ev-charger"
                >
                  <option value="unspecified">No especificado</option>
                  <option value="private">Cargador privado</option>
                  <option value="communal">Cargador disponible en el conjunto/edificio</option>
                  <option value="none">No cuenta</option>
                </select>
              </div>
            </div>
          )}

          {/* STEP 4 */}
          {step === 4 && (
            <div ref={(el) => { if (el) fieldRefs.current.images = el; }}>
              <h2 className="font-serif text-2xl font-bold text-habita-dark">Sube fotos</h2>
              <p className="text-sm text-gray-500 mt-2">
                Añade al menos 5 fotos para destacar tu publicación.
                <span className="ml-1 font-semibold text-habita-dark" data-testid="publish-photos-limit-hint">
                  Tu plan <span className="capitalize">{currentPlan.name}</span> permite hasta {maxPhotos} fotos ({data.images.length}/{maxPhotos} usadas).
                </span>
              </p>

              {fieldErrors.images && <FormError msg={fieldErrors.images} testid="field-error-images" className="mt-3" />}

              <label className={`mt-5 block border-2 border-dashed rounded-2xl p-10 text-center cursor-pointer transition-colors ${fieldErrors.images ? 'border-rose-400 hover:bg-rose-50' : 'border-habita hover:bg-habita-mint'} ${uploading || data.images.length >= maxPhotos ? 'opacity-60 pointer-events-none' : ''}`}>
                <input
                  type="file"
                  multiple
                  accept="image/jpeg,image/jpg,image/png,image/webp,image/heic,image/heif,.heic,.heif"
                  onChange={handleImages}
                  className="hidden"
                  disabled={uploading || data.images.length >= maxPhotos}
                  data-testid="publish-photos-input"
                />
                {uploading ? <Loader2 className="w-10 h-10 text-habita mx-auto animate-spin" /> : <Upload className="w-10 h-10 text-habita mx-auto" />}
                <p className="font-semibold mt-3 text-habita-dark">
                  {uploading
                    ? 'Subiendo imágenes...'
                    : data.images.length >= maxPhotos
                      ? `Alcanzaste el máximo de ${maxPhotos} fotos`
                      : 'Sube tus fotos'}
                </p>
                <p className="text-xs text-gray-500 mt-1">Arrastra o haz clic. JPG, PNG, WEBP, HEIC (iPhone) hasta 25MB.</p>
              </label>

              {/* Per-file progress indicator */}
              {uploadProgress.length > 0 && (
                <div className="mt-4 space-y-1.5" data-testid="upload-progress-list">
                  {uploadProgress.map((p) => (
                    <div key={p.idx} className="flex items-center gap-2 text-xs px-3 py-2 rounded-lg bg-gray-50">
                      {p.status === 'done' && <Check className="w-3.5 h-3.5 text-emerald-600 shrink-0" />}
                      {p.status === 'uploading' && <Loader2 className="w-3.5 h-3.5 text-habita animate-spin shrink-0" />}
                      {p.status === 'queued' && <div className="w-3.5 h-3.5 rounded-full border border-gray-300 shrink-0" />}
                      {p.status === 'error' && <AlertCircle className="w-3.5 h-3.5 text-rose-600 shrink-0" />}
                      <span className="flex-1 truncate text-gray-700">{p.name}</span>
                      <span className="text-gray-400">{(p.size / (1024 * 1024)).toFixed(1)} MB</span>
                      {p.status === 'error' && <span className="text-rose-600 ml-2 truncate max-w-[160px]">{p.error}</span>}
                    </div>
                  ))}
                </div>
              )}

              {data.images.length > 0 && (
                <div className="grid grid-cols-3 md:grid-cols-4 gap-3 mt-5">
                  {data.images.map((img, i) => (
                    <div key={i} className="relative aspect-square rounded-xl overflow-hidden group">
                      <img src={imgUrl(img)} alt="" className="w-full h-full object-cover" />
                      <button onClick={() => setField('images', data.images.filter((_, x) => x !== i))} className="absolute top-1.5 right-1.5 w-7 h-7 rounded-full bg-black/60 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                        <X className="w-3.5 h-3.5" />
                      </button>
                      {i === 0 && <span className="absolute bottom-1.5 left-1.5 bg-amber-500 text-white text-[10px] font-bold px-2 py-0.5 rounded">PORTADA</span>}
                    </div>
                  ))}
                </div>
              )}

              <VideoUploadSection
                videos={data.videos || []}
                onChange={(vids) => setField('videos', vids)}
              />
            </div>
          )}

          {/* STEP 5 */}
          {step === 5 && (
            <div className="space-y-5">
              <h2 className="font-serif text-2xl font-bold text-habita-dark">Precio y contacto</h2>
              <div className="grid md:grid-cols-2 gap-4">
                <div ref={(el) => { if (el) fieldRefs.current.price = el; }}>
                  <Label className="text-xs uppercase tracking-widest text-gray-500">
                    {data.operationMode === 'ambos' ? 'Precio de venta (COP)' : `Precio (${data.operation === 'arriendo' ? 'COP/mes' : 'COP'})`}
                  </Label>
                  <Input
                    type="number"
                    value={data.price}
                    onChange={e => setField('price', e.target.value)}
                    className={`mt-1.5 h-12 ${fieldErrors.price ? 'border-rose-500 focus-visible:ring-rose-400' : ''}`}
                    placeholder="450000000"
                    data-testid="publish-field-price"
                    aria-invalid={!!fieldErrors.price}
                    inputMode="numeric"
                  />
                  {data.price && !fieldErrors.price && <p className="text-xs text-habita mt-1">{formatCOP(data.price)}</p>}
                  {fieldErrors.price && <FormError msg={fieldErrors.price} testid="field-error-price" />}
                  <PricingHint
                    city={data.city}
                    neighborhood={data.neighborhood}
                    area={+data.area || 0}
                    stratum={+data.stratum || 0}
                    operation={data.operationMode === 'ambos' ? 'venta' : data.operation}
                    propertyType={data.type}
                    currentPrice={+data.price || 0}
                    onEstimate={setPricingEstimate}
                  />
                </div>
                {data.operationMode === 'ambos' ? (
                  <div ref={(el) => { if (el) fieldRefs.current.rentPrice = el; }}>
                    <Label className="text-xs uppercase tracking-widest text-gray-500">Precio de arriendo mensual (COP)</Label>
                    <Input
                      type="number"
                      value={data.rentPrice}
                      onChange={e => setField('rentPrice', e.target.value)}
                      className={`mt-1.5 h-12 ${fieldErrors.rentPrice ? 'border-rose-500 focus-visible:ring-rose-400' : ''}`}
                      placeholder="3000000"
                      data-testid="publish-field-rent-price"
                      aria-invalid={!!fieldErrors.rentPrice}
                      inputMode="numeric"
                    />
                    {data.rentPrice && !fieldErrors.rentPrice && <p className="text-xs text-habita mt-1">{formatCOP(data.rentPrice)} /mes</p>}
                    {fieldErrors.rentPrice && <FormError msg={fieldErrors.rentPrice} testid="field-error-rent-price" />}
                    <PricingHint
                      city={data.city}
                      neighborhood={data.neighborhood}
                      area={+data.area || 0}
                      stratum={+data.stratum || 0}
                      operation="arriendo"
                      propertyType={data.type}
                      currentPrice={+data.rentPrice || 0}
                    />
                  </div>
                ) : (
                  <div>
                    <Label className="text-xs uppercase tracking-widest text-gray-500">Administración (opcional)</Label>
                    <Input type="number" value={data.adminFee} onChange={e => setField('adminFee', e.target.value)} className="mt-1.5 h-12" placeholder="450000" inputMode="numeric" />
                  </div>
                )}
              </div>
              {data.operationMode === 'ambos' && (
                <div>
                  <Label className="text-xs uppercase tracking-widest text-gray-500">Administración (opcional)</Label>
                  <Input type="number" value={data.adminFee} onChange={e => setField('adminFee', e.target.value)} className="mt-1.5 h-12 md:max-w-[50%]" placeholder="450000" inputMode="numeric" />
                </div>
              )}
              <div className="pt-4 border-t">
                <h3 className="font-semibold text-habita-dark mb-3">Tus datos de contacto</h3>
                <div className="grid md:grid-cols-3 gap-4">
                  <Input value={data.contactName} onChange={e => setField('contactName', e.target.value)} placeholder="Nombre" className="h-12" />
                  <Input type="email" value={data.contactEmail} onChange={e => setField('contactEmail', e.target.value)} placeholder="Email" className="h-12" />
                  <Input type="tel" value={data.contactPhone} onChange={e => setField('contactPhone', e.target.value)} placeholder="Teléfono" className="h-12" />
                </div>
              </div>
              <div className="bg-habita-mint rounded-xl p-4 text-sm text-habita-dark">
                <strong>Resumen:</strong> {data.type || '—'} en {data.city || '—'} · {data.beds} hab · {data.baths} baños · {data.area}m²
                {data.operationMode === 'ambos'
                  ? <> · Venta {data.price ? formatCOP(data.price) : '—'} · Arriendo {data.rentPrice ? `${formatCOP(data.rentPrice)}/mes` : '—'}</>
                  : <> · {data.price ? formatCOP(data.price) : '—'}{data.operation === 'arriendo' ? '/mes' : ''}</>}
              </div>
            </div>
          )}

          {error && (
            <div className="bg-rose-50 border border-rose-200 text-rose-700 text-sm px-4 py-3 rounded-xl mt-5 flex items-start gap-2" data-testid="publish-error" role="alert">
              <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
              <span className="whitespace-pre-line flex-1">{error}</span>
            </div>
          )}

          <div className="flex justify-between gap-3 mt-10 pt-6 border-t">
            {step > 1 ? (
              <Button type="button" variant="outline" onClick={prev} disabled={publishing} className="rounded-full h-12 px-7 disabled:opacity-60"><ChevronLeft className="w-4 h-4 mr-1" /> Atrás</Button>
            ) : <div />}
            {step < 5 ? (
              <Button type="button" onClick={next} className="btn-habita rounded-full h-12 px-7" data-testid="publish-next-btn">Continuar <ChevronRight className="w-4 h-4 ml-1" /></Button>
            ) : (
              <Button
                type="button"
                onClick={publish}
                disabled={publishing || uploading}
                className="btn-habita rounded-full h-12 px-9 disabled:opacity-60 disabled:cursor-not-allowed"
                data-testid="publish-submit-btn"
                aria-busy={publishing}
              >
                {publishing
                  ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> {isEdit ? 'Guardando...' : 'Publicando...'}</>
                  : <>{isEdit ? 'Guardar cambios' : 'Publicar propiedad'} <Check className="w-4 h-4 ml-1" /></>}
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Price calibration modal — non-blocking, empathetic tone. */}
      <Dialog
        open={priceModal.open}
        onOpenChange={(open) => setPriceModal((m) => ({ ...m, open }))}
      >
        <DialogContent className="max-w-md" data-testid="price-modal">
          <DialogHeader>
            <div className="w-12 h-12 rounded-full bg-amber-100 text-amber-600 grid place-items-center mb-2">
              <TrendingUp className="w-6 h-6" />
            </div>
            <DialogTitle className="font-serif text-2xl text-habita-dark">
              Tu precio está {priceModal.overPct}% por encima del rango sugerido
            </DialogTitle>
            <DialogDescription className="text-sm text-gray-600 pt-2">
              Publicaciones dentro del rango del sector reciben en promedio <strong>3x más contactos</strong>. Si tu inmueble tiene acabados especiales, vista privilegiada o remodelaciones recientes, cuéntalo en la descripción para justificar el precio.
            </DialogDescription>
          </DialogHeader>
          <div className="bg-habita-mint/40 border border-habita/20 rounded-xl p-3 text-sm text-habita-dark">
            <p className="font-semibold flex items-center gap-1.5">
              <Sparkles className="w-4 h-4 text-habita" />
              Sugerencia del Advisor
            </p>
            <p className="mt-1 text-habita-dark/80 leading-snug">
              Precio máximo sugerido para tu sector: <strong>{formatCOP(priceModal.priceMax)}</strong>
              {data.operation === 'arriendo' && <span className="text-xs text-gray-500"> /mes</span>}
            </p>
          </div>
          <DialogFooter className="gap-2 sm:gap-2 flex-col sm:flex-row">
            <Button
              variant="outline"
              onClick={() => {
                // Ajustar precio → cerrar modal, foco al input de precio
                setPriceModal({ open: false, overPct: 0, priceMax: 0 });
                focusField('price');
              }}
              className="rounded-full h-11"
              data-testid="price-modal-adjust-btn"
            >
              Ajustar precio
            </Button>
            <Button
              onClick={() => {
                priceModalAcknowledgedRef.current = true;
                setPriceModal({ open: false, overPct: 0, priceMax: 0 });
                // Re-disparar publish inmediatamente; ya no volverá a mostrar el modal
                setTimeout(() => publish(), 50);
              }}
              className="btn-habita rounded-full h-11"
              data-testid="price-modal-continue-btn"
            >
              Continuar con mi precio
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal de upgrade EN CALIENTE — límite de plan alcanzado (403) */}
      <Dialog
        open={upgradeModal.open}
        onOpenChange={(open) => { if (!open && !upgrading) setUpgradeModal({ open: false, currentName: '', nextId: null, nextName: '' }); }}
      >
        <DialogContent className="max-w-md" data-testid="upgrade-modal">
          <DialogHeader>
            <div className="w-12 h-12 rounded-2xl bg-habita-mint flex items-center justify-center mb-1">
              <Sparkles className="w-6 h-6 text-habita" />
            </div>
            <DialogTitle className="font-serif text-2xl text-habita-dark">
              Alcanzaste el límite de tu plan {upgradeModal.currentName}
            </DialogTitle>
            <DialogDescription className="text-sm text-gray-600 pt-2">
              Cambia al plan <strong className="text-habita-dark">{upgradeModal.nextName}</strong> gratis durante la Beta para continuar.
              No perderás los datos que ya ingresaste.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-2 flex-col sm:flex-row">
            <Button
              variant="outline"
              onClick={() => setUpgradeModal({ open: false, currentName: '', nextId: null, nextName: '' })}
              disabled={upgrading}
              data-testid="upgrade-cancel-btn"
              className="rounded-full"
            >
              Ahora no
            </Button>
            <Button
              onClick={handleUpgrade}
              disabled={upgrading}
              data-testid="upgrade-confirm-btn"
              className="btn-habita rounded-full"
            >
              {upgrading
                ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Cambiando...</>
                : <>Cambiar a {upgradeModal.nextName} gratis</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Footer />
    </div>
  );
}

// -- Video upload sub-section --------------------------------------------
const MAX_VIDEO_MB = 50;
const MAX_VIDEO_SECONDS = 60;
const VIDEO_TYPES = /^video\/(mp4|quicktime|webm|x-m4v|x-quicktime)$/i;
const VIDEO_EXTS = /\.(mp4|mov|m4v|webm|qt)$/i;

function VideoUploadSection({ videos, onChange }) {
  const inputRef = React.useRef(null);
  const [uploading, setUploading] = React.useState(false);
  const [error, setError] = React.useState('');
  const { toast } = useToast();

  const probeDuration = (file) => new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const v = document.createElement('video');
    v.preload = 'metadata';
    v.muted = true;
    const cleanup = () => { URL.revokeObjectURL(url); };
    v.onloadedmetadata = () => { const d = v.duration; cleanup(); resolve(d || 0); };
    v.onerror = () => { cleanup(); resolve(0); };
    v.src = url;
    // Hard timeout — some videos take forever to probe
    setTimeout(() => { cleanup(); resolve(0); }, 8000);
  });

  const handleFile = async (file) => {
    setError('');
    if (!file) return;
    const okType = VIDEO_TYPES.test(file.type || '') || VIDEO_EXTS.test(file.name || '');
    if (!okType) {
      setError('Formato no soportado. Usa MP4, MOV o WebM.');
      return;
    }
    if (file.size > MAX_VIDEO_MB * 1024 * 1024) {
      setError(`El video pesa ${(file.size / 1024 / 1024).toFixed(1)} MB. Máximo ${MAX_VIDEO_MB} MB.`);
      return;
    }
    const dur = await probeDuration(file);
    if (dur && dur > MAX_VIDEO_SECONDS + 0.5) {
      setError(`El video dura ${dur.toFixed(0)}s. Máximo ${MAX_VIDEO_SECONDS}s.`);
      return;
    }
    setUploading(true);
    try {
      const res = await api.uploadVideo(file);
      onChange([...(videos || []), {
        url: res.url, public_id: res.public_id,
        thumbnail: res.thumbnail, duration: res.duration,
      }]);
      toast({ title: 'Video listo', description: 'Se subió correctamente a tu publicación.' });
    } catch (err) {
      const msg = friendlyError(err, 'No pudimos subir el video. Intenta con otro archivo o más corto.');
      setError(msg);
      toast({ title: 'Error subiendo video', description: msg, variant: 'destructive' });
    } finally {
      setUploading(false);
    }
  };

  const remove = async (i) => {
    const v = videos[i];
    if (v?.public_id) {
      try { await api.deleteVideo(v.public_id); } catch { /* best-effort cleanup */ }
    }
    onChange(videos.filter((_, x) => x !== i));
  };

  return (
    <div className="mt-8 border-t pt-7" data-testid="video-upload-section">
      <h3 className="font-serif text-xl font-bold text-habita-dark">Videos (opcional)</h3>
      <p className="text-sm text-gray-500 mt-1.5">Sube hasta {MAX_VIDEO_SECONDS}s de recorrido. MP4, MOV o WebM, máx. {MAX_VIDEO_MB} MB.</p>

      <label
        className={`mt-4 block border-2 border-dashed border-habita rounded-2xl p-8 text-center cursor-pointer hover:bg-habita-mint transition-colors ${uploading ? 'opacity-60 pointer-events-none' : ''}`}
      >
        <input
          ref={inputRef}
          type="file"
          accept="video/mp4,video/quicktime,video/webm,.mp4,.mov,.webm,.m4v"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) handleFile(f);
            e.target.value = '';
          }}
          className="hidden"
          disabled={uploading}
          data-testid="publish-video-input"
        />
        {uploading
          ? <Loader2 className="w-9 h-9 text-habita mx-auto animate-spin" />
          : <Camera className="w-9 h-9 text-habita mx-auto" />}
        <p className="font-semibold mt-3 text-habita-dark">
          {uploading ? 'Subiendo video...' : 'Sube un video corto'}
        </p>
        <p className="text-xs text-gray-500 mt-1">Arrastra o haz clic · iPhone, Android, Reels, TikTok</p>
      </label>

      {error && (
        <p className="text-xs text-rose-600 mt-2 flex items-center gap-1">
          <AlertCircle className="w-3.5 h-3.5" /> {error}
        </p>
      )}

      {videos && videos.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-5" data-testid="published-videos-grid">
          {videos.map((v, i) => (
            <div key={v.public_id || v.url || i} className="relative aspect-video rounded-xl overflow-hidden group bg-black">
              {v.thumbnail ? (
                <img src={v.thumbnail} alt="" className="w-full h-full object-cover" loading="lazy" />
              ) : (
                <video src={v.url} className="w-full h-full object-cover" muted playsInline preload="metadata" />
              )}
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <div className="w-12 h-12 rounded-full bg-white/80 backdrop-blur flex items-center justify-center">
                  <span className="block w-0 h-0 border-y-[8px] border-y-transparent border-l-[12px] border-l-habita-dark ml-1" />
                </div>
              </div>
              {v.duration ? (
                <span className="absolute bottom-1.5 right-1.5 bg-black/70 text-white text-[10px] font-bold px-2 py-0.5 rounded">
                  {Math.round(v.duration)}s
                </span>
              ) : null}
              <button
                type="button"
                onClick={() => remove(i)}
                data-testid={`remove-video-${i}`}
                className="absolute top-1.5 right-1.5 w-7 h-7 rounded-full bg-black/60 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


function PricingHint({ city, neighborhood, area, stratum, operation, propertyType, currentPrice, onEstimate }) {
  const [state, setState] = useState({ status: 'idle', data: null });

  useEffect(() => {
    if (!city || !area || area < 10) { setState({ status: 'idle', data: null }); onEstimate?.(null); return; }
    const t = setTimeout(async () => {
      setState({ status: 'loading', data: null });
      try {
        const res = await api.estimatePricing({
          city,
          neighborhood: neighborhood || null,
          property_type: propertyType || null,
          operation: operation || 'venta',
          area,
          stratum: stratum || null,
        });
        setState({ status: res?.matched ? 'ok' : 'nomatch', data: res });
        onEstimate?.(res || null);
      } catch {
        setState({ status: 'nomatch', data: { message: 'Calculando promedio del sector...' } });
        onEstimate?.(null);
      }
    }, 450);
    return () => clearTimeout(t);
  }, [city, neighborhood, area, stratum, operation, propertyType, onEstimate]);

  if (state.status === 'idle') {
    return (
      <p className="text-[11px] text-gray-400 mt-2 italic">
        Ingresa el barrio y el área para ver el precio sugerido por Habita.
      </p>
    );
  }
  if (state.status === 'loading') {
    return (
      <p className="text-xs text-gray-500 mt-2 flex items-center gap-1.5" data-testid="pricing-hint-loading">
        <Loader2 className="w-3 h-3 animate-spin text-habita" />
        Consultando precio sugerido...
      </p>
    );
  }

  const d = state.data || {};
  // Mostrar el precio SIEMPRE que exista un rango, aunque no sea match exacto de barrio.
  // Solo mostramos el mensaje sin precio si el backend realmente no devolvió rango.
  if (!d.price_min || !d.price_max) {
    return (
      <div className="mt-2 flex items-start gap-2 text-xs text-gray-600" data-testid="pricing-hint-nomatch">
        <Sparkles className="w-3.5 h-3.5 text-habita mt-0.5 shrink-0" />
        <span>{d.message || 'Calculando promedio del sector...'}</span>
      </div>
    );
  }

  const inRange = currentPrice > 0 && d.price_min && d.price_max
    && currentPrice >= d.price_min * 0.9 && currentPrice <= d.price_max * 1.1;
  const above = currentPrice > 0 && d.price_max && currentPrice > d.price_max * 1.1;
  const below = currentPrice > 0 && d.price_min && currentPrice < d.price_min * 0.9;

  return (
    <div
      className="mt-2 rounded-lg border border-habita/20 bg-habita-mint/40 p-2.5 text-xs text-habita-dark"
      data-testid="pricing-hint-ok"
    >
      <p className="leading-snug" data-testid="pricing-hint-message">
        <Sparkles className="w-3.5 h-3.5 text-habita inline mr-1" />
        {d.message}
      </p>
      {(above || below || inRange) && (
        <p
          className={`text-[11px] mt-1 font-medium ${above ? 'text-amber-700' : below ? 'text-rose-600' : 'text-habita'}`}
          data-testid="pricing-hint-verdict"
        >
          {inRange && '✓ Tu precio está dentro del rango sugerido.'}
          {above && '↑ Tu precio está por encima del rango — asegúrate de justificarlo con acabados o vista.'}
          {below && '↓ Tu precio está por debajo del rango — podrías estar dejando dinero sobre la mesa.'}
        </p>
      )}
    </div>
  );
}
